import {toast} from 'sonner';
import {signOut} from '../store/slices/authThunks';
import {debugWarn} from './debug';

/**
 * Any dispatch that can take a thunk. Deliberately not `AppDispatch`: importing it from `store.ts`
 * would close a cycle (store → slices → here → store), and the `dispatch` a thunk is handed is typed
 * against `unknown` state, which `AppDispatch` does not accept.
 */
type ThunkCapableDispatch = (action: any) => unknown;

/** Pull the HTTP status out of the error strings the thunks throw. */
function extractStatus(errorStr: string): number | null {
  const patterns = [
    /returned error (\d{3})\b/i,   // `Worker API returned error 403: {...}`
    /\berror (\d{3})\b/i,
    /\bstatus:? (\d{3})\b/i,       // `Download failed with status 404: ...`
    /:\s*(\d{3})\s*-\s/,           // `Failed to delete transcription: 404 - ...`
    /\((\d{3})\)/,                 // `Failed to fetch subscriptions (404)`
  ];

  for (const pattern of patterns) {
    const match = errorStr.match(pattern);
    if (match) {
      return Number(match[1]);
    }
  }

  return null;
}

/** A refusal about *this resource*, not about the session. Never a reason to sign out. */
function isOwnershipRefusal(errorStr: string): boolean {
  return /do(es)? not own|not own this|forbidden|not authorized to access/i.test(errorStr);
}

/**
 * Decides whether a failed request means "this session is dead" (⇒ sign out) or just "this request
 * was refused" (⇒ show the error and stay signed in).
 *
 * **The distinction is 401 vs 403, and it is not cosmetic.** The worker answers a request for
 * somebody else's row with `403 {"error":"Unauthorized: You do not own this project"}`. This
 * function used to match on the bare word "unauthorized", so that 403 read as an expired session and
 * silently signed the user out — which is what turned one stale project id into a logout loop when
 * switching accounts. A 403 says the token is *valid* and the answer is still no; signing out is
 * exactly the wrong response.
 *
 * Status codes are recovered from the message because the thunks throw formatted strings rather than
 * carrying the response through. Anything with a known status that is not 401 is never a logout.
 */
export function shouldLogout(error: unknown): boolean {
  const errorStr = String(error);

  // An explicit instruction from the backend outranks everything else.
  try {
    const match = errorStr.match(/\{.*"logout".*\}/);
    if (match) {
      const errorObj = JSON.parse(match[0]);
      if (errorObj.logout === true) {
        return true;
      }
    }
  } catch {
    // Not JSON, fall through to the status checks.
  }

  // "You do not own this project" is a valid session being told no.
  if (isOwnershipRefusal(errorStr)) {
    return false;
  }

  const status = extractStatus(errorStr);
  if (status !== null) {
    return status === 401;
  }

  // No status in the message (a few thunks throw the raw response body). Fall back to the phrases
  // that only ever come from a rejected token.
  return /unauthorized|invalid or expired token|invalid jwt|jwt expired|missing authorization|invalid api key/i
    .test(errorStr);
}

/**
 * End the session and say so. Use where the caller already knows the status is 401 and does not
 * rethrow (so `handleAuthFailure` below never sees the error).
 */
export function signOutExpiredSession(dispatch: ThunkCapableDispatch, context?: string): void {
  debugWarn(`${context ? `[${context}] ` : ''}Session rejected (401) - signing out`);
  toast.error('Your session has expired. Please log in again.', {duration: 5000});
  dispatch(signOut());
}

/**
 * The one place a request failure turns into a sign-out.
 *
 * Every thunk funnels its catch block through here rather than repeating
 * `if (shouldLogout(e)) dispatch(signOut())`, so the user is always *told* the session ended — being
 * dropped back on the login screen with no explanation was the other half of this bug.
 *
 * Returns whether it signed out, so callers can skip their own error toast.
 */
export function handleAuthFailure(error: unknown, dispatch: ThunkCapableDispatch, context?: string): boolean {
  if (!shouldLogout(error)) {
    return false;
  }

  debugWarn(`${context ? `[${context}] ` : ''}Session rejected by:`, String(error));
  signOutExpiredSession(dispatch, context);
  return true;
}

/**
 * Check if an error indicates authentication failure and, if so, log the user out.
 *
 * @deprecated Use `handleAuthFailure`, which also tells the user what happened.
 */
export function handleAuthError(error: unknown, dispatch: ThunkCapableDispatch): boolean {
  return handleAuthFailure(error, dispatch);
}
